import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import type { EoaProvisioningPhase } from '../eoaProvisioningCeremony';
import {
    PROVISIONING_HINT,
    provisioningEstimateMs,
    provisioningLeg,
    provisioningProgress,
} from '../provisioningProgress';

const TICK_MS = 500;

interface ProvisioningProgressProps {
    /** The running ceremony phase; the signing ones render no bar. */
    phase: EoaProvisioningPhase | null;
    /** The backend is running the detached HyperEVM creation right now. */
    detached: boolean;
    /** Both wallets landed: the bar fills instead of asymptoting. */
    complete: boolean;
    /** Something is waiting on the user: freeze the bar rather than animate it. */
    failed: boolean;
    className?: string;
}

/**
 * Time-based progress for whichever provisioning leg is running.
 *
 * Neither leg has a real progress signal to report, so this is a clock against
 * the chain budget the leg is spending (see `../provisioningProgress`), which
 * makes a wait that is working look different from a wait that is stuck.
 *
 * The timer restarts per leg, because they are separate transactions on separate
 * chains and one bar spanning both would be wrong for each. It fills only when
 * the account is actually finished; an intermediate leg landing is reported by
 * its checklist row turning green, and the bar starts over on the next leg.
 */
const ProvisioningProgress: React.FC<ProvisioningProgressProps> = ({
    phase,
    detached,
    complete,
    failed,
    className,
}) => {
    const estimateMs = provisioningEstimateMs(phase, detached);
    const leg = provisioningLeg(phase, detached);
    // the leg identity, with the detached run folded onto the phase it continues
    // so handing the HyperEVM creation to the backend does not restart the bar.
    const legKey = phase ?? (detached ? 'hyperevm-provisioning' : '');

    const startedAtRef = useRef<{ key: string; at: number }>({
        key: legKey,
        at: Date.now(),
    });
    if (startedAtRef.current.key !== legKey) {
        startedAtRef.current = { key: legKey, at: Date.now() };
    }

    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        // a signature prompt, a finished account and a failure are all states
        // nothing advances out of on a clock, so none of them runs a timer.
        if (!estimateMs || complete || failed) return;
        const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
        return () => clearInterval(timer);
    }, [estimateMs, legKey, complete, failed]);

    const { percent, overrun } = provisioningProgress(
        startedAtRef.current.at,
        nowMs,
        estimateMs,
        { complete },
    );
    // The failure clears the phase, which takes the estimate with it. Holding the
    // last live reading is what lets the bar stop where the run stopped instead
    // of vanishing, so the error copy has something to sit under.
    const shownRef = useRef(0);
    if (!failed) shownRef.current = percent;
    const shownPercent = failed ? shownRef.current : percent;

    // nothing has run yet: an empty bar under an unpressed button promises work
    // that is not happening.
    if (!estimateMs && !complete && shownPercent === 0) return null;

    return (
        <div className={cn('w-full', className)}>
            <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(shownPercent)}
                aria-label="Account setup progress"
                className="h-1 w-full overflow-hidden rounded-full bg-primary/15"
            >
                <div
                    className={cn(
                        'h-full rounded-full transition-[width] duration-500 ease-linear motion-reduce:transition-none',
                        failed
                            ? 'bg-destructive/70'
                            : complete
                                ? 'bg-success'
                                : 'bg-primary/70',
                        // past the estimate the bar cannot advance any further,
                        // so it pulses instead: the movement is what says it is
                        // still working when the percentage cannot.
                        overrun &&
                            !failed &&
                            'animate-pulse motion-reduce:animate-none',
                    )}
                    style={{ width: `${shownPercent}%` }}
                />
            </div>
            {leg && !complete && !failed && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                    {PROVISIONING_HINT[leg]}
                </p>
            )}
        </div>
    );
};

ProvisioningProgress.displayName = 'ProvisioningProgress';

export default ProvisioningProgress;
